// src/pages/customers/AddCustomerPage.tsx
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  Loader2,
  Plus,
  Router,
  ShieldCheck,
  User,
  Wifi,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../components/ui/Toast";
import { API_CONFIG, fetchApp } from "../../services/apiService";
import { PlanLite } from "../../types/customers";

interface RouterLite {
  id: string | number;
  name: string;
  auth_method?: "api" | "radius" | string;
  ip_address?: string | null;
}

interface NewCustomerForm {
  router_id: string;
  username: string;
  account_number: string;
  password: string;
  phone_number: string;
  email: string;
  plan_id: string;
  amount_payable: string;
  status: boolean;
  service_expires_at: string;
}

interface CurrentUserMe {
  id: string | number;
  username: string;
  email: string;
  client_id: string | number;
  routers?: [];
  role?: { id: string | number; name: string };
}

type Tone = "emerald" | "sky" | "amber" | "indigo" | "slate";

const toneClasses: Record<Tone, string> = {
  emerald: "bg-emerald-100 text-emerald-700",
  sky: "bg-sky-100 text-sky-700",
  amber: "bg-amber-100 text-amber-700",
  indigo: "bg-indigo-100 text-indigo-700",
  slate: "bg-slate-200 text-slate-600",
};

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13.5px] font-medium text-slate-800 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13.5px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  description?: string;
  tone?: Tone;
}> = ({ icon, title, description, tone = "slate" }) => (
  <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5">
    <div
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${toneClasses[tone]}`}
    >
      {icon}
    </div>

    <div className="min-w-0">
      <p className="text-[13.5px] font-black text-slate-800">{title}</p>

      {description && (
        <p className="mt-0.5 text-[11.5px] font-medium text-slate-500">
          {description}
        </p>
      )}
    </div>
  </div>
);

const Field: React.FC<{
  label: string;
  hint?: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
  span2?: boolean;
}> = ({ label, hint, required, children, span2 }) => (
  <div
    className={["flex flex-col gap-1.5", span2 ? "sm:col-span-2" : ""]
      .filter(Boolean)
      .join(" ")}
  >
    <label className="flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-slate-400">
      {label}
      {required && <span className="text-rose-500">*</span>}
    </label>

    {children}

    {hint && <div className="mt-0.5">{hint}</div>}
  </div>
);

const Hint: React.FC<{
  children: React.ReactNode;
  variant?: "default" | "warn";
}> = ({ children, variant = "default" }) => (
  <p
    className={`flex items-start gap-1.5 text-[11.5px] font-medium leading-snug ${
      variant === "warn" ? "text-amber-700" : "text-slate-500"
    }`}
  >
    {variant === "warn" && (
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
    )}
    {children}
  </p>
);

const AddCustomerPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);

  const [routers, setRouters] = useState<RouterLite[]>([]);
  const [plans, setPlans] = useState<PlanLite[]>([]);

  const [form, setForm] = useState<NewCustomerForm>({
    router_id: "",
    username: "",
    account_number: "",
    password: "",
    phone_number: "",
    email: "",
    plan_id: "",
    amount_payable: "",
    status: true,
    service_expires_at: "",
  });

  const storedUser: CurrentUserMe = JSON.parse(
    localStorage.getItem("user") || "{}",
  );

  const CLIENT_ID = storedUser?.client_id;

  const formatKES = (value: number) =>
    `KES ${new Intl.NumberFormat("en-KE").format(value)}`;

  const normalizeList = <T,>(res: any): T[] => {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.items)) return res.items;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.routers)) return res.routers;
    if (Array.isArray(res?.results)) return res.results;
    return [];
  };

  const getErrorMessage = (err: any, fallback: string) => {
    if (!err) return fallback;
    if (typeof err === "string") return err;
    if (err?.message) return err.message;

    if (err?.detail) {
      if (typeof err.detail === "string") return err.detail;
      return JSON.stringify(err.detail);
    }

    if (err?.error) return err.error;

    return fallback;
  };

  const fetchWithFallback = async <T,>(urls: string[]): Promise<T[]> => {
    let lastError: any = null;

    for (const url of urls) {
      try {
        const res = await fetchApp(url);
        const list = normalizeList<T>(res);

        if (list.length > 0 || res) {
          return list;
        }
      } catch (err) {
        lastError = err;
      }
    }

    if (lastError) throw lastError;

    return [];
  };

  const selectedRouter = useMemo(() => {
    return routers.find((r) => String(r.id) === String(form.router_id));
  }, [routers, form.router_id]);

  const selectedPlan = useMemo(() => {
    return plans.find((p) => String(p.id) === String(form.plan_id)) ?? null;
  }, [plans, form.plan_id]);

  const selectedAuthMethod = String(
    selectedRouter?.auth_method || "api",
  ).toLowerCase();

  const handleChange = (
    field: keyof NewCustomerForm,
    value: string | boolean,
  ) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const loadOptions = async () => {
    setLoadingOptions(true);

    try {
      const [routersList, plansList] = await Promise.all([
        fetchWithFallback<RouterLite>([
          `${API_CONFIG.baseUrl}/routers/?client_id=${encodeURIComponent(
            String(CLIENT_ID),
          )}`,
        ]),
        fetchWithFallback<any>([
          `${API_CONFIG.baseUrl}/plans`,
          `${API_CONFIG.baseUrl}/plans/`,
        ]),
      ]);

      setRouters(routersList);

      const normalizedPlans: PlanLite[] = plansList.map((p: any) => ({
        id: p.id,
        name: p.name,
        speed: p.speed ?? p.rate_limit ?? p.bandwidth ?? null,
        price:
          p.price != null
            ? Number(p.price)
            : p.price_cents != null
              ? Number(p.price_cents) / 100
              : p.plan_price_cents != null
                ? Number(p.plan_price_cents) / 100
                : 0,
      }));

      setPlans(normalizedPlans);
    } catch (err: any) {
      console.error(err);
      showToast(
        getErrorMessage(err, "Failed to load routers and plans."),
        "error",
      );
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateForm = () => {
    const routerId = form.router_id.trim();
    const customerName = form.username.trim();
    const accountNumber = form.account_number.trim();
    const password = form.password.trim();
    const amountPayable = Number(form.amount_payable);

    if (!routerId) {
      showToast("Please select a router.", "error");
      return false;
    }

    if (!customerName) {
      showToast("Customer name is required.", "error");
      return false;
    }

    if (!accountNumber) {
      showToast("Account number / PPPoE username is required.", "error");
      return false;
    }

    if (!password) {
      showToast("PPPoE password is required.", "error");
      return false;
    }

    if (!form.amount_payable.trim() || Number.isNaN(amountPayable)) {
      showToast("Amount payable is required.", "error");
      return false;
    }

    if (amountPayable <= 0) {
      showToast("Amount payable must be greater than 0.", "error");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const routerId = form.router_id.trim();
    const customerName = form.username.trim();
    const accountNumber = form.account_number.trim();
    const phoneNumber = form.phone_number.trim() || accountNumber;
    const email = form.email.trim();
    const amountPayable = Number(form.amount_payable);

    try {
      setSaving(true);

      const body = {
        router_id: routerId,
        username: customerName,
        account_number: accountNumber,
        password: form.password.trim(),
        phone_number: phoneNumber,
        email: email || null,
        plan_id: form.plan_id || null,
        amount_payable: amountPayable,
        status: form.status,
        service_expires_at: form.service_expires_at
          ? new Date(form.service_expires_at).toISOString()
          : null,
      };

      const res = await fetchApp(
        `${API_CONFIG.baseUrl}/secrets/add-and-sync?router_id=${encodeURIComponent(
          routerId,
        )}`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );

      if (res && typeof res === "object" && (res as any).error) {
        throw new Error((res as any).error);
      }

      showToast("Customer added successfully.", "success");
      navigate("/customers");
    } catch (err: any) {
      console.error(err);
      showToast(getErrorMessage(err, "Failed to add customer."), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1400px] space-y-5 sm:space-y-6">
        <button
          type="button"
          onClick={() => navigate("/customers")}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-50 px-3 py-1.5 text-[12.5px] font-bold text-slate-500 shadow-sm transition-colors hover:bg-slate-100 hover:text-slate-800"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Customers
        </button>

        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Customer Provisioning
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  Add PPPoE Customer
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  Create a customer and provision them using the selected router
                  method: MikroTik API or RADIUS.
                </p>
              </div>

              <div className="rounded-3xl border border-emerald-200 bg-emerald-50/90 px-4 py-3 shadow-sm shadow-emerald-200/40 lg:max-w-md">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                    <ShieldCheck className="h-4 w-4" />
                  </div>

                  <p className="text-[12.5px] font-medium leading-snug text-emerald-800">
                    Backend will automatically decide between{" "}
                    <strong>API</strong> or <strong>RADIUS</strong> based on the
                    chosen router&apos;s auth method.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {loadingOptions ? (
          <div className="flex min-h-[45vh] flex-col items-center justify-center gap-3 rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
            <div className="flex h-14 w-14 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/40">
              <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />
            </div>

            <p className="text-[13px] font-bold text-slate-500">
              Loading routers and plans…
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2 lg:gap-6">
              <div className="space-y-5 lg:space-y-6">
                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-lg shadow-slate-300/25">
                  <SectionHeader
                    icon={<Router className="h-4 w-4" />}
                    tone="amber"
                    title="Router Selection"
                    description="Select the router to provision this user to"
                  />

                  <div className="p-4 sm:p-6">
                    <Field label="Router & Provisioning Method" required>
                      <div className="relative">
                        <select
                          value={form.router_id}
                          onChange={(e) =>
                            handleChange("router_id", e.target.value)
                          }
                          className={`${inputCls} cursor-pointer appearance-none pr-9`}
                          required
                        >
                          <option value="">— Select Router —</option>

                          {routers.map((r) => (
                            <option key={String(r.id)} value={String(r.id)}>
                              {r.name || `Router #${r.id}`}
                            </option>
                          ))}
                        </select>

                        <svg
                          className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>

                      {selectedRouter && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-black ${
                              selectedAuthMethod === "radius"
                                ? "border-violet-200 bg-violet-100 text-violet-800"
                                : "border-sky-200 bg-sky-100 text-sky-800"
                            }`}
                          >
                            <Router className="h-3.5 w-3.5" />
                            {selectedAuthMethod === "radius"
                              ? "RADIUS"
                              : "MikroTik API"}
                          </span>

                          {selectedRouter.ip_address && (
                            <span className="rounded-full bg-slate-200 px-3 py-1.5 font-mono text-[11.5px] font-bold text-slate-600">
                              IP: {selectedRouter.ip_address}
                            </span>
                          )}
                        </div>
                      )}
                    </Field>
                  </div>
                </div>

                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-lg shadow-slate-300/25">
                  <SectionHeader
                    icon={<User className="h-4 w-4" />}
                    tone="sky"
                    title="Identity & Contact"
                    description="Basic profile and communication details"
                  />

                  <div className="p-4 sm:p-6">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field label="Customer Full Name" required span2>
                        <input
                          type="text"
                          className={inputCls}
                          value={form.username}
                          onChange={(e) =>
                            handleChange("username", e.target.value)
                          }
                          placeholder="e.g. John Mwangi"
                          required
                        />
                      </Field>

                      <Field
                        label="Phone Number"
                        hint={<Hint>Used for SMS/M-Pesa interactions.</Hint>}
                      >
                        <input
                          type="tel"
                          className={inputCls}
                          value={form.phone_number}
                          onChange={(e) =>
                            handleChange("phone_number", e.target.value)
                          }
                          placeholder="2547…"
                        />
                      </Field>

                      <Field label="Email Address">
                        <input
                          type="email"
                          className={inputCls}
                          value={form.email}
                          onChange={(e) =>
                            handleChange("email", e.target.value)
                          }
                          placeholder="john@example.com"
                        />
                      </Field>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-5 lg:space-y-6">
                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-lg shadow-slate-300/25">
                  <SectionHeader
                    icon={<Wifi className="h-4 w-4" />}
                    tone="emerald"
                    title="PPPoE & Subscription"
                    description="Network credentials, status, and internet plan"
                  />

                  <div className="p-4 sm:p-6">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field label="PPPoE Username / Account No." required>
                        <input
                          type="text"
                          className={inputCls}
                          value={form.account_number}
                          onChange={(e) =>
                            handleChange("account_number", e.target.value)
                          }
                          placeholder="e.g. john123"
                          required
                        />
                      </Field>

                      <Field label="PPPoE Password" required>
                        <input
                          type="text"
                          className={inputCls}
                          value={form.password}
                          onChange={(e) =>
                            handleChange("password", e.target.value)
                          }
                          placeholder="••••••"
                          autoComplete="off"
                          required
                        />
                      </Field>

                      <Field label="Initial Account Status" span2>
                        <div className="grid grid-cols-2 gap-2.5 pt-0.5">
                          <button
                            type="button"
                            onClick={() => handleChange("status", true)}
                            className={`flex items-center justify-center gap-2 rounded-2xl border py-2.5 text-[13px] font-black transition-all ${
                              form.status
                                ? "border-emerald-200 bg-emerald-100 text-emerald-800 shadow-sm shadow-emerald-200/50"
                                : "border-slate-300 bg-slate-50 text-slate-500 hover:bg-slate-100"
                            }`}
                          >
                            <span
                              className={`h-2 w-2 rounded-full ${
                                form.status ? "bg-emerald-500" : "bg-slate-300"
                              }`}
                            />
                            Active
                          </button>

                          <button
                            type="button"
                            onClick={() => handleChange("status", false)}
                            className={`flex items-center justify-center gap-2 rounded-2xl border py-2.5 text-[13px] font-black transition-all ${
                              !form.status
                                ? "border-slate-300 bg-slate-200 text-slate-700 shadow-sm"
                                : "border-slate-300 bg-slate-50 text-slate-500 hover:bg-slate-100"
                            }`}
                          >
                            <span
                              className={`h-2 w-2 rounded-full ${
                                !form.status ? "bg-slate-600" : "bg-slate-300"
                              }`}
                            />
                            Inactive
                          </button>
                        </div>
                      </Field>

                      <Field label="Internet Plan" span2>
                        <div className="relative">
                          <select
                            className={`${inputCls} cursor-pointer appearance-none pr-9`}
                            value={form.plan_id}
                            onChange={(e) =>
                              handleChange("plan_id", e.target.value)
                            }
                          >
                            <option value="">— No Plan Selected —</option>

                            {plans.map((p) => (
                              <option key={String(p.id)} value={String(p.id)}>
                                {p.name} {p.speed ? `(${p.speed})` : ""} —{" "}
                                {p.price != null
                                  ? formatKES(Number(p.price))
                                  : ""}
                              </option>
                            ))}
                          </select>

                          <svg
                            className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>

                        {selectedPlan && (
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <span className="rounded-full border border-sky-200 bg-sky-100 px-3 py-1.5 text-[11.5px] font-black text-sky-800">
                              {selectedPlan.name}
                            </span>

                            {selectedPlan.speed && (
                              <span className="rounded-full border border-slate-300 bg-slate-200 px-3 py-1.5 text-[11.5px] font-bold text-slate-600">
                                {selectedPlan.speed}
                              </span>
                            )}

                            <span className="rounded-full border border-emerald-200 bg-emerald-100 px-3 py-1.5 text-[11.5px] font-black text-emerald-800">
                              {formatKES(Number(selectedPlan.price || 0))}
                            </span>
                          </div>
                        )}
                      </Field>

                      <Field
                        label="Amount Payable"
                        required
                        span2
                        hint={
                          <Hint>
                            This is the amount this PPPoE account will pay per
                            billing cycle.
                          </Hint>
                        }
                      >
                        <input
                          type="number"
                          min="1"
                          step="1"
                          className={inputCls}
                          value={form.amount_payable}
                          onChange={(e) =>
                            handleChange("amount_payable", e.target.value)
                          }
                          placeholder="e.g. 1500"
                          required
                        />
                      </Field>
                    </div>
                  </div>
                </div>

                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-lg shadow-slate-300/25">
                  <SectionHeader
                    icon={<Calendar className="h-4 w-4" />}
                    tone="indigo"
                    title="Service Expiry"
                    description="Optional expiration anchor for automated billing"
                  />

                  <div className="p-4 sm:p-6">
                    <Field label="Service Expiry Date">
                      <input
                        type="datetime-local"
                        className={inputCls}
                        value={form.service_expires_at}
                        onChange={(e) =>
                          handleChange("service_expires_at", e.target.value)
                        }
                      />
                    </Field>

                    <div className="mt-4 rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3">
                      <Hint>
                        Leave this blank if you want the system to calculate the
                        expiry automatically from the billing cycle.
                      </Hint>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 z-10 -mx-3 border-t border-slate-200 bg-[#eef3f8]/90 px-3 py-4 backdrop-blur-xl sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
              <div className="mx-auto flex max-w-[1400px] flex-col-reverse gap-2.5 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => navigate("/customers")}
                  disabled={saving}
                  className={`${btnBase} h-11 w-full border border-slate-300 bg-slate-50 px-5 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800 sm:w-auto`}
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className={`${btnBase} h-11 w-full bg-gradient-to-r from-emerald-500 to-teal-500 px-7 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600 sm:w-auto`}
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}

                  {saving ? "Adding Customer…" : "Add Customer"}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddCustomerPage;
